import { useState } from 'react';
import { Text } from '@chakra-ui/react';

import { ModalColumns } from '../../../atoms/modal';
import { DateInputWithConfirm } from '../../../formInputs/DateInput';
import { TextareaWithConfirm } from '../../../formInputs/Textarea';
import { RadioOptionsWithConfirm } from '../../../formInputs/RadioOptions';
import UploadActions from '../../../atoms/upload-actions';
import UploadExample from '../../../atoms/upload-example';

import UploadCompleteSection from '../../../molecules/uploadComplete';
import AcknowledgeBox from '../../../molecules/acknowledgeBox';

import { isValidDateAndPast } from '../../../../utilities/validation';
import { IQuestion, Q } from '../../../../types';
import { getDateFromString, getStringFromDate } from '../../../../utilities/helpers';
import example from '../../../../assets/images/sample-student-visa-refusal.jpg';
import { RequirementsRemedy } from '../../../atoms/RequirementsRemedy';

export interface IProps {
  complete: boolean;
  status: string;
  fileNames: string[];
  isNonUkVisaRefusal: boolean;
  hasVisaRefusalDoc?: boolean;
  hasNoVisaRefusalDocReason?: string;
  refusalDate?: string;
  onContinue: () => void;
  onUpload: (metadata?: any) => void;
  onClose: () => void;
  onVisaRefusalMetaDataUpdate: (metadata?: any) => void;
  docKey: string;
  infoOnly: boolean;
  docStatus: string;
  onAcknowledgeToggle: (status: string) => void;
}

const VisaRefusalInnerModal = (props: IProps) => {
  const [refusalDate, setRefusalDate] = useState(getDateFromString(props.refusalDate || ''));

  const [refusalDateError, setRefusalDateError] = useState('');
  const [hasVisaRefusalDoc, setHasVisaRefusalDoc] = useState(props.hasVisaRefusalDoc);
  const [hasNoVisaRefusalDocReason, setHasNoVisaRefusalDocReason] = useState(props.hasNoVisaRefusalDocReason);

  const { infoOnly, docStatus, docKey, isNonUkVisaRefusal } = props;
  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  const toggleAcknowledge = (_key: string, value: boolean) => {
    // prevent call to API if previous call not yet complete
    if ((value === true && docStatus !== 'ACKNOWLEDGED') || (value === false && docStatus === 'ACKNOWLEDGED')) {
      props.onAcknowledgeToggle(value === true ? 'ACKNOWLEDGED' : 'NOT_ACKNOWLEDGED');
    }
  };

  const questionsComplete =
    (hasVisaRefusalDoc &&
      refusalDate.DD !== '' &&
      refusalDate.DD !== null &&
      refusalDate.MM !== '' &&
      refusalDate.MM !== null &&
      refusalDate.YYYY !== '' &&
      refusalDate.YYYY !== null &&
      refusalDateError === '') ||
    (hasVisaRefusalDoc === false && hasNoVisaRefusalDocReason !== undefined && hasNoVisaRefusalDocReason !== '');

  const handleUpload = () => {
    props.onUpload({
      refusalDate: getStringFromDate(refusalDate),
      hasVisaRefusalDoc,
    });
  };

  const onSaveAndReturn = () => {
    props.onVisaRefusalMetaDataUpdate({
      hasVisaRefusalDoc,
      hasNoVisaRefusalDocReason,
    });
    props.onClose();
  };

  return (
    <>
      {infoOnly ? (
        <>
          <Text data-selector={'upload-intro'} mb={6}>
            You have indicated that you have a previous {isNonUkVisaRefusal ? 'Non-' : null}UK visa refusal. This
            document may be required during your visa application.
          </Text>

          <AcknowledgeBox
            id={docKey}
            blurb="Please confirm you have read and understand the above."
            isTicked={docStatus === 'ACKNOWLEDGED'}
            onChange={toggleAcknowledge}
          />

          <UploadActions
            complete={docStatus === 'ACKNOWLEDGED'}
            onContinue={handleOnClose}
            label="Return to document upload"
          />
        </>
      ) : (
        <>
          {props.complete ? (
            <UploadCompleteSection fileNames={props.fileNames} onUpload={handleUpload} onContinue={props.onContinue} />
          ) : (
            <>
              <RadioOptionsWithConfirm
                id={'has-doc-copy'}
                label="Do you still have a copy of this document available for upload?"
                questions={[
                  {
                    char: 'Y',
                    label: 'Yes',
                    selected: hasVisaRefusalDoc === true,
                  },
                  {
                    char: 'N',
                    label: 'No',
                    selected: hasVisaRefusalDoc === false,
                  },
                ]}
                isInline={true}
                onClick={(question: IQuestion) => setHasVisaRefusalDoc(question.label === 'Yes')}
              />

              {hasVisaRefusalDoc === true ? (
                <>
                  <DateInputWithConfirm
                    id={'refusal-date'}
                    key={`${Q.PREV_VISA_REFUSAL_DATE}`}
                    label={'What is the date of this refusal? (as per the document)'}
                    errorText={refusalDateError}
                    value={refusalDate}
                    onChangeDD={(evt: any) => {
                      setRefusalDate({
                        ...refusalDate,
                        DD: evt.currentTarget.value,
                      });
                    }}
                    onChangeMM={(evt: any) => {
                      setRefusalDate({
                        ...refusalDate,
                        MM: evt.currentTarget.value,
                      });
                    }}
                    onChangeYY={(evt: any) => {
                      setRefusalDate({
                        ...refusalDate,
                        YYYY: evt.currentTarget.value,
                      });
                    }}
                    onBlur={() => {
                      if (!isValidDateAndPast(refusalDate)) {
                        setRefusalDateError(`Date must be valid and in the past`);
                      }
                    }}
                    onFocus={() => {
                      if (refusalDateError !== '') {
                        setRefusalDateError('');
                      }
                    }}
                  />

                  <UploadActions complete={questionsComplete} label={'Save & Continue'} onContinue={handleUpload} />
                </>
              ) : null}

              {hasVisaRefusalDoc === false ? (
                <>
                  <TextareaWithConfirm
                    id={'has-no-doc-reason'}
                    label={'Please give a reason why you do not have the document:'}
                    onChange={(evt: any) => setHasNoVisaRefusalDocReason(evt.currentTarget.value)}
                    value={hasNoVisaRefusalDocReason}
                  />

                  {hasNoVisaRefusalDocReason && (
                    <>
                      <RequirementsRemedy isSatisfied>
                        This item will be marked as Done. You may be contacted for more information.
                      </RequirementsRemedy>

                      <UploadActions
                        complete={questionsComplete}
                        label={'Save & Continue'}
                        onContinue={onSaveAndReturn}
                      />
                    </>
                  )}
                </>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
};

const VisaRefusalModal: React.FC<IProps> = (props: IProps) => {
  const { docStatus, isNonUkVisaRefusal } = props;
  const handleOnClose = () => {
    if (docStatus === 'INFO_ONLY') {
      props.onAcknowledgeToggle('NOT_ACKNOWLEDGED');
    }
    props.onClose();
  };

  return (
    <ModalColumns
      id={'visaRefusal'}
      title={`${isNonUkVisaRefusal ? 'Non-UK Visa Refusal' : 'Visa Refusal'}`}
      onClose={handleOnClose}
      hideFooter
      supportingColumn={<UploadExample altText="Visa refusal example" url={example} />}
    >
      <VisaRefusalInnerModal {...props} />
    </ModalColumns>
  );
};

export default VisaRefusalModal;
